import { useState, type FormEvent } from 'react';
import { CLIENT_TYPES } from '../../data/domain';
import type { Client, LatLng } from '../../data/types';
import { uid } from '../../lib/format';
import { putEntity } from '../../sync/outbox';
import { AddressLookup } from '../../ui/Lookups';
import { Modal } from '../../ui/Modal';

export function ClientModal({ client, onClose, onSaved }: { client: Client | null; onClose(): void; onSaved(client: Client): void }) {
  const [name, setName] = useState(client?.name ?? '');
  const [type, setType] = useState(client?.type ?? 'Residential');
  const [contact, setContact] = useState(client?.contact ?? '');
  const [phone, setPhone] = useState(client?.phone ?? '');
  const [email, setEmail] = useState(client?.email ?? '');
  const [address, setAddress] = useState(client?.address ?? '');
  const [location, setLocation] = useState<LatLng | null>(client?.location ?? null);
  const [highRisk, setHighRisk] = useState(client?.highRisk ?? false);
  const [notes, setNotes] = useState(client?.notes ?? '');
  const [error, setError] = useState('');

  async function save(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return setError('Enter the client or business name.');
    const entry: Client = {
      id: client?.id ?? uid(`${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40)}-`).slice(0, 80),
      name: trimmed, type, highRisk, contact: contact.trim(), phone: phone.trim(), email: email.trim(), address: address.trim(), notes: notes.trim(),
      // Cleared whenever the address is retyped; set again when a map result is picked.
      location
    };
    await putEntity('clients', entry, `${client ? 'Updated' : 'New'} client ${entry.name}`);
    onSaved(entry);
  }

  return (
    <Modal id="clientModal" onClose={onClose} labelledBy="clientModalTitle">
      <form onSubmit={save}>
        <p className="eyebrow" id="clientModalEyebrow">{client ? 'Client record' : 'New client'}</p>
        <h2 id="clientModalTitle">{client ? 'Edit client' : 'Add client'}</h2>
        <div className="form-row">
          <label>Client or business name <input id="clientName" placeholder="Client or business name" value={name} onChange={(event) => setName(event.target.value)} autoFocus /></label>
          <label>Client type <select id="clientType" value={type} onChange={(event) => { setType(event.target.value); if (event.target.value === 'High risk') setHighRisk(true); }}>{CLIENT_TYPES.map((item) => <option key={item}>{item}</option>)}</select></label>
        </div>
        <div className="form-row">
          <label>Contact name <input id="clientContact" placeholder="Full name" value={contact} onChange={(event) => setContact(event.target.value)} /></label>
          <label>Phone <input id="clientPhone" placeholder="04xx xxx xxx" value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
        </div>
        <label>Email <input id="clientEmail" type="email" placeholder="name@example.com" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Service address <AddressLookup id="clientAddress" placeholder="Street, suburb, postcode" value={address} onChange={(value) => { setAddress(value); setLocation(null); }} onPick={(result) => setLocation(result.location)} /></label>
        <label className="check"><input id="clientHighRisk" type="checkbox" checked={highRisk} onChange={(event) => setHighRisk(event.target.checked)} /> This premises is a high-risk or sensitive place</label>
        <label>Site notes <textarea id="clientNotes" placeholder="Access, pets, hazards, service preferences…" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="form-actions"><button className="btn btn-primary" id="saveClient" type="submit">Save client</button></div>
      </form>
    </Modal>
  );
}
